import React from 'react';

/**
 * Framing thumbnails — rendered-style SVGs showing how each option
 * COMPOSES the subject. Subject is a consistent cube across all
 * variants (square face, same dimensions) so the differentiator is
 * the framing around it — text placeholder lines, crop markers, a
 * plinth, a rotation ring, etc. — not the cube's apparent size or
 * shape. Only `angled-hero` shows rotation (3/4 view with depth);
 * every other variant shows the cube front-facing.
 *
 * Design rules:
 *   - 120×90 viewBox, 4:3 aspect.
 *   - Reference cube: 30×30 square face, d=0 (front-facing only).
 *     Angled-hero overrides d=14. Macro-detail intentionally uses a
 *     much larger cube bleeding off the frame since macro IS zoom.
 *   - Shared warm neutral palette.
 *   - Thumbnail IS the frame — no dashed viewport outline chrome.
 */

const baseStyle: React.CSSProperties = {
    width: '100%',
    aspectRatio: '4/3',
    display: 'block',
};

const HORIZON = 62;
const CUBE_W = 30;
const CUBE_H = 30;
const CUBE_CX = 60;

const Floor: React.FC<{ fill: string }> = ({ fill }) => (
    <rect x="0" y={HORIZON} width="120" height={90 - HORIZON} fill={fill} />
);

/**
 * Parametric cube.
 *   cx, yBottom      — center of the bottom-front edge (grounds the cube
 *                      on the floor at yBottom).
 *   w, h             — front face width and height.
 *   d                — perspective depth. 0 renders a front-facing cube
 *                      (just the front face visible). Larger values
 *                      reveal the top + right faces (3/4 view).
 *   gradPrefix       — unique per-instance so gradient ids don't collide.
 */
const Cube: React.FC<{
    cx: number;
    yBottom: number;
    w: number;
    h: number;
    d?: number;
    gradPrefix: string;
}> = ({ cx, yBottom, w, h, d = 0, gradPrefix }) => {
    const x = cx - w / 2;
    const fbl = { x, y: yBottom };
    const fbr = { x: x + w, y: yBottom };
    const ftl = { x, y: yBottom - h };
    const ftr = { x: x + w, y: yBottom - h };
    const dy = d * 0.55;
    const bbr = { x: fbr.x + d, y: fbr.y - dy };
    const btr = { x: ftr.x + d, y: ftr.y - dy };
    const btl = { x: ftl.x + d, y: ftl.y - dy };
    const gf = `${gradPrefix}-f`;
    const gs = `${gradPrefix}-s`;
    const gt = `${gradPrefix}-t`;
    const showDepth = d > 0;
    return (
        <>
            <defs>
                <linearGradient id={gf} x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#CECDC9" />
                    <stop offset="100%" stopColor="#8A8987" />
                </linearGradient>
                {showDepth && (
                    <>
                        <linearGradient id={gs} x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#6A6967" />
                            <stop offset="100%" stopColor="#3E3D3B" />
                        </linearGradient>
                        <linearGradient id={gt} x1="0%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" stopColor="#EEEDEA" />
                            <stop offset="100%" stopColor="#B0AFAC" />
                        </linearGradient>
                    </>
                )}
            </defs>
            {showDepth && (
                <>
                    <polygon
                        points={`${ftl.x},${ftl.y} ${ftr.x},${ftr.y} ${btr.x},${btr.y} ${btl.x},${btl.y}`}
                        fill={`url(#${gt})`}
                    />
                    <polygon
                        points={`${fbr.x},${fbr.y} ${bbr.x},${bbr.y} ${btr.x},${btr.y} ${ftr.x},${ftr.y}`}
                        fill={`url(#${gs})`}
                    />
                </>
            )}
            <polygon
                points={`${fbl.x},${fbl.y} ${fbr.x},${fbr.y} ${ftr.x},${ftr.y} ${ftl.x},${ftl.y}`}
                fill={`url(#${gf})`}
            />
        </>
    );
};

const Shadow: React.FC<{ cx: number; rx?: number; opacity?: number }> = ({
    cx,
    rx = 17,
    opacity = 0.22,
}) => (
    <ellipse cx={cx} cy={HORIZON + 1.5} rx={rx} ry={3} fill="#1F2022" opacity={opacity} />
);

// ─── studio cover ────────────────────────────────────────────────
// EDITORIAL straight-on shot — same cube as all other straight-on
// variants, but with thin display-text placeholder lines above it,
// selling the "hero composition with room for a cover headline".
export const StudioCoverFraming: React.FC = () => (
    <svg viewBox="0 0 120 90" style={baseStyle}>
        <rect x="0" y="0" width="120" height="90" fill="#F4F4F2" />
        <Floor fill="#E4E2DE" />
        <line x1={28} y1={14} x2={92} y2={14} stroke="#A8A7A4" strokeWidth="1.5" strokeLinecap="round" opacity="0.55" />
        <line x1={38} y1={20} x2={82} y2={20} stroke="#A8A7A4" strokeWidth="0.9" strokeLinecap="round" opacity="0.45" />
        <Shadow cx={CUBE_CX} />
        <Cube cx={CUBE_CX} yBottom={HORIZON} w={CUBE_W} h={CUBE_H} gradPrefix="fr-studio" />
    </svg>
);

// ─── catalog ─────────────────────────────────────────────────────
// FUNCTIONAL straight-on shot — same cube, but surrounded by tight
// L-shaped corner crop markers that signal "product-listing crop".
export const CatalogFraming: React.FC = () => (
    <svg viewBox="0 0 120 90" style={baseStyle}>
        <rect x="0" y="0" width="120" height="90" fill="#F4F4F2" />
        <Floor fill="#E4E2DE" />
        {/* crop markers pulled in close to the cube's footprint */}
        <path d="M 34 24 L 34 30 M 34 24 L 40 24" stroke="#7A7876" strokeWidth="1" strokeLinecap="round" />
        <path d="M 86 24 L 86 30 M 86 24 L 80 24" stroke="#7A7876" strokeWidth="1" strokeLinecap="round" />
        <path d="M 34 70 L 34 64 M 34 70 L 40 70" stroke="#7A7876" strokeWidth="1" strokeLinecap="round" />
        <path d="M 86 70 L 86 64 M 86 70 L 80 70" stroke="#7A7876" strokeWidth="1" strokeLinecap="round" />
        <Shadow cx={CUBE_CX} />
        <Cube cx={CUBE_CX} yBottom={HORIZON} w={CUBE_W} h={CUBE_H} gradPrefix="fr-catalog" />
    </svg>
);

// ─── macro detail ────────────────────────────────────────────────
// Extreme close-up — enormous cube positioned so only its front-top-
// right corner (where three faces meet) falls inside the frame. The
// depth offset is kept so the viewer actually sees three faces and
// their edges, making it read as "zoomed into a 3D object" rather
// than "zoomed into a flat surface". Reference cube size is
// deliberately broken here because macro IS zoom.
export const MacroDetailFraming: React.FC = () => (
    <svg viewBox="0 0 120 90" style={baseStyle}>
        <rect x="0" y="0" width="120" height="90" fill="#F4F4F2" />
        <Cube cx={0} yBottom={130} w={100} h={100} d={22} gradPrefix="fr-macro" />
    </svg>
);

// ─── overhead flat-lay ───────────────────────────────────────────
// Top-down view of a FLAT SAMPLE (tile / plank) laid on a tabletop —
// not a cube viewed from above. This framing is surface-only in the
// seed, so the subject is a thin material swatch, never a 3D object.
// Subtle veining marks the surface as "material sample"; editorial
// styling props sit at the frame edges for flat-lay composition.
export const OverheadFlatlayFraming: React.FC = () => (
    <svg viewBox="0 0 120 90" style={baseStyle}>
        <defs>
            <linearGradient id="fr-flatlay-sample" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#EEEDEA" />
                <stop offset="100%" stopColor="#ACAAA6" />
            </linearGradient>
        </defs>
        <rect x="0" y="0" width="120" height="90" fill="#E8E7E3" />
        {/* flat sample viewed straight down + thin hairline edge */}
        <rect
            x={CUBE_CX - CUBE_W / 2}
            y={30}
            width={CUBE_W}
            height={CUBE_H}
            rx={0.5}
            fill="url(#fr-flatlay-sample)"
            stroke="#8A8986"
            strokeWidth="0.5"
        />
        {/* subtle organic veining — sells "material sample", not plain square */}
        <path
            d="M 48 38 Q 56 36 62 42 T 74 44"
            stroke="#999894"
            strokeWidth="0.4"
            fill="none"
            opacity="0.55"
        />
        <path
            d="M 46 52 Q 58 49 70 54"
            stroke="#999894"
            strokeWidth="0.4"
            fill="none"
            opacity="0.45"
        />
        <path
            d="M 50 60 Q 58 58 66 62"
            stroke="#999894"
            strokeWidth="0.35"
            fill="none"
            opacity="0.35"
        />
        {/* thin ambient contact shadow on the table */}
        <rect
            x={CUBE_CX - CUBE_W / 2}
            y={30 + CUBE_H + 0.3}
            width={CUBE_W}
            height={1.2}
            fill="#1F2022"
            opacity="0.18"
        />
        {/* corner "focus" brackets at all four corners — camera-
            viewfinder marks; immediately signals "framing" + "top-down
            view" before anything else registers. */}
        <g stroke="#7A7876" strokeWidth="1" strokeLinecap="round" fill="none" opacity="0.7">
            <path d="M 6 14 L 6 6 L 14 6" />
            <path d="M 114 14 L 114 6 L 106 6" />
            <path d="M 6 76 L 6 84 L 14 84" />
            <path d="M 114 76 L 114 84 L 106 84" />
        </g>

        {/* TOP label — spells out that this is a straight-overhead view
            so the vendor doesn't have to decode the composition. */}
        <text
            x={60}
            y={12}
            fontSize="6.5"
            fill="#4A4945"
            fontFamily="system-ui, -apple-system, sans-serif"
            fontWeight={700}
            textAnchor="middle"
            letterSpacing="2"
        >
            TOP
        </text>

    </svg>
);

// ═════════════════════════════════════════════════════════════════
// SURFACE variants — tile-subject versions of the universal framings.
// Used when the linked product is a surface material (tile / plank /
// hardwood) instead of furniture. The cube subject doesn't match:
// surface products are FLAT samples, not volumetric 3D pieces.
// Routing is done in RecipeNode.getIllustrationForOption via
// productVisualizationType.
// ═════════════════════════════════════════════════════════════════

/**
 * Tile subject — thin flat material sample. Same palette, veining,
 * and hairline border as the flat-lay sample so vendors read "this
 * is my tile" consistently across cards.
 */
const Tile: React.FC<{
    cx: number;
    yBottom: number;
    w: number;
    h: number;
    gradPrefix: string;
}> = ({ cx, yBottom, w, h, gradPrefix }) => {
    const x = cx - w / 2;
    const y = yBottom - h;
    const gId = `${gradPrefix}-tile`;
    return (
        <>
            <defs>
                <linearGradient id={gId} x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#EEEDEA" />
                    <stop offset="100%" stopColor="#ACAAA6" />
                </linearGradient>
            </defs>
            <rect
                x={x}
                y={y}
                width={w}
                height={h}
                rx={0.5}
                fill={`url(#${gId})`}
                stroke="#8A8986"
                strokeWidth="0.5"
            />
            <path
                d={`M ${x + 2} ${y + h * 0.32} Q ${x + w * 0.45} ${y + h * 0.22} ${x + w - 2} ${y + h * 0.36}`}
                stroke="#999894"
                strokeWidth="0.4"
                fill="none"
                opacity="0.55"
            />
            <path
                d={`M ${x + 2} ${y + h * 0.66} Q ${x + w * 0.5} ${y + h * 0.6} ${x + w - 2} ${y + h * 0.7}`}
                stroke="#999894"
                strokeWidth="0.4"
                fill="none"
                opacity="0.45"
            />
        </>
    );
};

// ─── studio cover (surface) ──────────────────────────────────────
export const StudioCoverFramingSurface: React.FC = () => (
    <svg viewBox="0 0 120 90" style={baseStyle}>
        <rect x="0" y="0" width="120" height="90" fill="#F4F4F2" />
        <Floor fill="#E4E2DE" />
        <line x1={28} y1={14} x2={92} y2={14} stroke="#A8A7A4" strokeWidth="1.5" strokeLinecap="round" opacity="0.55" />
        <line x1={38} y1={20} x2={82} y2={20} stroke="#A8A7A4" strokeWidth="0.9" strokeLinecap="round" opacity="0.45" />
        <Shadow cx={CUBE_CX} />
        <Tile cx={CUBE_CX} yBottom={HORIZON} w={CUBE_W} h={CUBE_H} gradPrefix="fr-studio-surf" />
    </svg>
);

// ─── catalog (surface) ───────────────────────────────────────────
export const CatalogFramingSurface: React.FC = () => (
    <svg viewBox="0 0 120 90" style={baseStyle}>
        <rect x="0" y="0" width="120" height="90" fill="#F4F4F2" />
        <Floor fill="#E4E2DE" />
        <path d="M 34 24 L 34 30 M 34 24 L 40 24" stroke="#7A7876" strokeWidth="1" strokeLinecap="round" />
        <path d="M 86 24 L 86 30 M 86 24 L 80 24" stroke="#7A7876" strokeWidth="1" strokeLinecap="round" />
        <path d="M 34 70 L 34 64 M 34 70 L 40 70" stroke="#7A7876" strokeWidth="1" strokeLinecap="round" />
        <path d="M 86 70 L 86 64 M 86 70 L 80 70" stroke="#7A7876" strokeWidth="1" strokeLinecap="round" />
        <Shadow cx={CUBE_CX} />
        <Tile cx={CUBE_CX} yBottom={HORIZON} w={CUBE_W} h={CUBE_H} gradPrefix="fr-catalog-surf" />
    </svg>
);

// ─── macro detail (surface) ──────────────────────────────────────
// Zoomed so close to the tile that the surface veining becomes the
// subject — no edges, no silhouette, just the pattern.
export const MacroDetailFramingSurface: React.FC = () => (
    <svg viewBox="0 0 120 90" style={baseStyle}>
        <defs>
            <linearGradient id="fr-macro-surf" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#EEEDEA" />
                <stop offset="100%" stopColor="#8A8987" />
            </linearGradient>
        </defs>
        <rect x="0" y="0" width="120" height="90" fill="url(#fr-macro-surf)" />
        {/* pronounced veining — the tile surface IS the whole frame */}
        <path d="M -5 22 Q 40 18 80 28 T 128 32" stroke="#8A7258" strokeWidth="1.4" fill="none" opacity="0.45" />
        <path d="M -5 44 Q 50 38 100 48 T 130 52" stroke="#8A7258" strokeWidth="1.2" fill="none" opacity="0.4" />
        <path d="M -5 64 Q 45 60 90 70 T 130 72" stroke="#8A7258" strokeWidth="1" fill="none" opacity="0.35" />
        <path d="M 10 78 Q 55 75 100 82" stroke="#8A7258" strokeWidth="0.9" fill="none" opacity="0.3" />
    </svg>
);

// ─── angled hero (surface) ───────────────────────────────────────
// Simple 30×30 tile (same reference size as every other surface
// framing) wrapped by the SAME elliptical rotation ring used by the
// cube angled-hero card — dashed back half drawn before the tile,
// solid front half drawn over, arrowhead pointing in the direction
// of travel. The ring carries the rotation story; the tile stays a
// plain face-on square so it reads as "my tile, rotated" instead of
// a stretched trapezoid.
export const AngledHeroFramingSurface: React.FC = () => {
    const ringCx = CUBE_CX;
    const ringCy = HORIZON - 2;
    const ringRx = 28;
    const ringRy = 7.5;
    const L = { x: ringCx - ringRx, y: ringCy };
    const R = { x: ringCx + ringRx, y: ringCy };
    const T = { x: ringCx, y: ringCy - ringRy };
    const B = { x: ringCx, y: ringCy + ringRy };
    const RING = '#5F5E5B';
    return (
        <svg viewBox="0 0 120 90" style={baseStyle}>
            <rect x="0" y="0" width="120" height="90" fill="#F4F4F2" />
            <Floor fill="#E4E2DE" />

            {/* BACK half of ring — dashed + faded, occluded by the
                tile where they overlap in paint order. */}
            <path
                d={`M ${L.x} ${L.y} Q ${T.x} ${T.y} ${R.x} ${R.y}`}
                fill="none"
                stroke={RING}
                strokeWidth="1"
                strokeLinecap="round"
                strokeDasharray="2.5 2.5"
                opacity="0.5"
            />

            <Shadow cx={CUBE_CX} rx={17} opacity={0.2} />

            <Tile
                cx={CUBE_CX}
                yBottom={HORIZON}
                w={CUBE_W}
                h={CUBE_H}
                gradPrefix="fr-angled-surf"
            />

            {/* FRONT half of ring + arrowhead — drawn AFTER the tile
                so it wraps around the near side. Arrowhead at the
                left endpoint points up-left along the tangent. */}
            <path
                d={`M ${R.x} ${R.y} Q ${B.x} ${B.y} ${L.x} ${L.y}`}
                fill="none"
                stroke={RING}
                strokeWidth="1.1"
                strokeLinecap="round"
            />
            <path
                d={`M ${L.x} ${L.y} L ${L.x + 4.5} ${L.y - 3} M ${L.x} ${L.y} L ${L.x + 4} ${L.y + 3}`}
                fill="none"
                stroke={RING}
                strokeWidth="1.1"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
};

export const FRAMING_ILLUSTRATIONS_SURFACE: Record<string, React.FC> = {
    'studio-cover': StudioCoverFramingSurface,
    'catalog': CatalogFramingSurface,
    'macro-detail': MacroDetailFramingSurface,
    'angled-hero': AngledHeroFramingSurface,
    // overhead-flatlay + pedestal are already tile-based in the main
    // export above (they're surface-only in the seed), so no surface
    // override needed here — the main export is the canonical version.
};

// ─── angled hero ─────────────────────────────────────────────────
// The ONLY variant with depth — 30×30 cube with d=14 (strong 3/4
// view), wrapped by an elliptical rotation ring. Dashed back half
// is drawn first (occluded by the cube), solid front half is drawn
// last (wraps over the near side). Arrowhead shows rotation
// direction; `~30°` label sits above.
export const AngledHeroFraming: React.FC = () => {
    const d = 14;
    // In 3/4 view the back corners offset RIGHT by d, so the cube's
    // visual bounding box extends further right than its front face.
    // Shift the cube's front-face center LEFT by d/2 so the overall
    // visual center lands on the thumbnail's center (CUBE_CX).
    const cubeCx = CUBE_CX - d / 2;
    // The rotation ring sits at the cube's footprint centroid. After
    // the shift above that centroid lies exactly at (CUBE_CX, ringCy).
    const ringCx = CUBE_CX;
    const ringCy = HORIZON - (d * 0.55) / 2;
    const ringRx = 32;
    const ringRy = 8.5;
    const L = { x: ringCx - ringRx, y: ringCy };
    const R = { x: ringCx + ringRx, y: ringCy };
    const T = { x: ringCx, y: ringCy - ringRy };
    const B = { x: ringCx, y: ringCy + ringRy };
    const RING = '#5F5E5B';
    return (
        <svg viewBox="0 0 120 90" style={baseStyle}>
            <rect x="0" y="0" width="120" height="90" fill="#F4F4F2" />
            <Floor fill="#E4E2DE" />

            {/* BACK half of ring — dashed + faded, will be occluded
                where the cube's front face paints over it. */}
            <path
                d={`M ${L.x} ${L.y} Q ${T.x} ${T.y} ${R.x} ${R.y}`}
                fill="none"
                stroke={RING}
                strokeWidth="1"
                strokeLinecap="round"
                strokeDasharray="2.5 2.5"
                opacity="0.5"
            />

            {/* ground shadow under the cube's footprint centroid */}
            <Shadow cx={ringCx} rx={19} />

            <Cube cx={cubeCx} yBottom={HORIZON} w={CUBE_W} h={CUBE_H} d={d} gradPrefix="fr-angled" />

            {/* FRONT half — solid, drawn over the cube. Arrowhead at
                the left endpoint points up-left, matching the tangent
                of travel along the front of the ellipse. */}
            <path
                d={`M ${R.x} ${R.y} Q ${B.x} ${B.y} ${L.x} ${L.y}`}
                fill="none"
                stroke={RING}
                strokeWidth="1.1"
                strokeLinecap="round"
            />
            <path
                d={`M ${L.x} ${L.y} L ${L.x + 4.5} ${L.y - 3} M ${L.x} ${L.y} L ${L.x + 4} ${L.y + 3}`}
                fill="none"
                stroke={RING}
                strokeWidth="1.1"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
};

// ─── pedestal ────────────────────────────────────────────────────
// Museum-style display for surface products: a FLAT TILE STANDING
// UPRIGHT on a small white plinth. Not a cube — pedestal framing
// is surface-only and the subject is a thin material sample shown
// face-on so the vendor's pattern / color / finish is front and
// centre. Tile is sized to match the flat-lay sample (CUBE_W ×
// CUBE_H = 30×30) so the subject feels like "the same tile, just
// standing up" when a vendor compares flat-lay vs pedestal cards.
// Subtle overhead glow evokes gallery lighting.
export const PedestalFraming: React.FC = () => {
    const plinthTop = 52;
    const plinthBot = 62;
    const plinthW = 36;
    const tileW = CUBE_W;
    const tileH = CUBE_H;
    const tileTop = plinthTop - tileH;
    return (
        <svg viewBox="0 0 120 90" style={baseStyle}>
            <defs>
                <linearGradient id="fr-pedestal-tile" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#CECDC9" />
                    <stop offset="100%" stopColor="#8A8987" />
                </linearGradient>
                {/* faint overhead light cone — reads as gallery lighting */}
                <radialGradient id="fr-pedestal-light" cx="50%" cy="-5%" r="65%">
                    <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.3" />
                    <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
                </radialGradient>
            </defs>
            <rect x="0" y="0" width="120" height="90" fill="#F2F2EF" />
            <rect x="0" y="0" width="120" height="60" fill="url(#fr-pedestal-light)" />
            <Floor fill="#E4E2DE" />

            <Shadow cx={CUBE_CX} rx={18} opacity={0.22} />

            {/* plinth — simple white block with thin right-edge shadow
                so it reads as a solid rectangular pedestal. */}
            <rect
                x={CUBE_CX - plinthW / 2}
                y={plinthTop}
                width={plinthW}
                height={plinthBot - plinthTop}
                fill="#FAFAF9"
            />
            <rect
                x={CUBE_CX + plinthW / 2 - 2}
                y={plinthTop}
                width={2}
                height={plinthBot - plinthTop}
                fill="#C2C1BD"
            />

            {/* contact shadow where tile meets plinth */}
            <ellipse
                cx={CUBE_CX}
                cy={plinthTop + 0.4}
                rx={tileW / 2}
                ry={1.1}
                fill="#1F2022"
                opacity="0.18"
            />

            {/* tile standing upright, thin and tall — material sample
                face-on so pattern / color / finish is legible. */}
            <rect
                x={CUBE_CX - tileW / 2}
                y={tileTop}
                width={tileW}
                height={tileH}
                fill="url(#fr-pedestal-tile)"
                stroke="#8A8986"
                strokeWidth="0.5"
            />
            {/* thin dark strip on the right edge — 3D hint without
                breaking the "no depth" rule applied elsewhere. */}
            <rect
                x={CUBE_CX + tileW / 2 - 1}
                y={tileTop}
                width={1}
                height={tileH}
                fill="#6B5A47"
                opacity="0.3"
            />
            {/* subtle organic veining — reads as stone / wood sample */}
            <path
                d={`M ${CUBE_CX - tileW / 2 + 2} ${tileTop + 12} Q ${CUBE_CX} ${tileTop + 8} ${CUBE_CX + tileW / 2 - 3} ${tileTop + 14}`}
                stroke="#8A8986"
                strokeWidth="0.4"
                fill="none"
                opacity="0.5"
            />
            <path
                d={`M ${CUBE_CX - tileW / 2 + 2} ${tileTop + 26} Q ${CUBE_CX} ${tileTop + 24} ${CUBE_CX + tileW / 2 - 3} ${tileTop + 30}`}
                stroke="#8A8986"
                strokeWidth="0.4"
                fill="none"
                opacity="0.4"
            />
        </svg>
    );
};

export const FRAMING_ILLUSTRATIONS: Record<string, React.FC> = {
    'studio-cover': StudioCoverFraming,
    'catalog': CatalogFraming,
    'macro-detail': MacroDetailFraming,
    'overhead-flatlay': OverheadFlatlayFraming,
    'angled-hero': AngledHeroFraming,
    'pedestal': PedestalFraming,
};
